import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { formatNumber } from '@/shared/utils/format/formatNumber.mjs';
import { countOutcomes } from '@/shared/utils/game/eventFilters.mjs';
import { EVENT_STATUS } from '@/shared/enums/events.mjs';
import { FACTION_INDEX } from '@/shared/enums/factions.mjs';
import AnimatedStat from '@/shared/components/AnimatedStat/AnimatedStat';
import './StatGrid.css';

/**
 * Format a Unix-seconds timestamp as a "DD MONTH" label (e.g. "25 JANUARY",
 * "01 MARCH"). Day is zero-padded; month is the full English name uppercased.
 * Fixed to UTC so the server-rendered output and client hydration agree
 * regardless of the viewer's timezone.
 */
function formatStartDate(unixSeconds) {
  const d = new Date(unixSeconds * 1000);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC'
  }).toUpperCase();
  return `${day} ${month}`;
}
function accidentalRateTooltip(accidentals, deaths) {
  const a = Number(accidentals || 0);
  const d = Number(deaths || 0);
  return `${formatNumber(a)} accidental / ${formatNumber(d)} total deaths`;
}

/**
 * The ▲ / ▼ / ▪ trend arrow shared by the delta-style stat subtitles.
 * `n` is a signed delta: positive → green ▲, negative → red ▼, zero → a
 * neutral, un-tinted ▪. The ▲/▼ triangles sit below their optical centre
 * so they're lifted 1.5px; ▪ is already centred in its em box and isn't
 * nudged.
 */
function deltaArrow(n) {
  const glyph = n > 0 ? '▲' : n < 0 ? '▼' : '▪';
  const colorClass = n > 0 ? 'text-success' : n < 0 ? 'text-danger' : '';
  const nudgeClass = n !== 0 ? '-translate-y-[1.5px]' : '';
  return <span className={`${nudgeClass} ${colorClass}`}>{glyph}</span>;
}

/**
 * Format the "LAST 24H" delta subtitle for the ONLINE card. Compares the
 * current player count to the 24h rolling-average baseline and shows the
 * gap with a ▲/▼/▪ arrow. Returns null only when there's no baseline yet
 * (new season).
 */
function playersDeltaSubtitle(currentPlayers, avgPlayers) {
  if (avgPlayers == null) return null;
  const delta = currentPlayers - avgPlayers;
  return <span className="inline-flex items-center gap-1.5 tracking-wide text-ghost uppercase">
            {deltaArrow(delta)}
            <span>
                <AnimatedStat value={Math.abs(delta)} />
            </span>
            <span>Last 24h</span>
        </span>;
}

/**
 * Subtitle for the ENEMIES_KILLED card. From the live cumulative kill total
 * and two historical baselines it derives two consecutive 24h kill volumes —
 * last24h = current − kills(24h ago), prev24h = kills(24h ago) − kills(48h
 * ago) — and shows the last-24h volume with a ▲/▼/▪ arrow marking whether the
 * killing pace rose, fell, or held versus the previous 24h. With no 48h
 * baseline yet (season 24–48h old) the pace can't be compared, so the arrow
 * is a neutral ▪. Returns null with no 24h baseline, or when nothing was
 * killed in the last 24h.
 */
function killsTrendSubtitle(current, baseline) {
  if (baseline?.ago24h == null) return null;
  // `current` may be a Prisma BigInt (kills are BIGINT in schema) while the
  // baselines are plain numbers — coerce before subtracting to avoid the
  // "Cannot mix BigInt and other types" runtime error during SSR.
  const last24h = Number(current) - Number(baseline.ago24h);
  if (last24h <= 0) return null;
  const prev24h = baseline.ago48h != null ? Number(baseline.ago24h) - Number(baseline.ago48h) : null;
  const trend = prev24h == null ? 0 : last24h - prev24h;
  return <span className="inline-flex items-center gap-1.5 tracking-wide text-ghost uppercase">
            {deltaArrow(trend)}
            <span>
                <AnimatedStat value={last24h} />
            </span>
            <span>Last 24h</span>
        </span>;
}

/**
 * Subtitle for the HELLDIVERS_LOST card — shows the absolute number of
 * accidental ("teamkill") deaths, marked with a small `backstab` icon and
 * a `MARTYRS` label (these are the divers who were teamkilled). Returns
 * null when there are no accidentals to report (either deaths or
 * accidentals is 0). The accidental + total death counts are also
 * surfaced via the card's tooltip.
 */
function accidentalSubtitle(accidentals, deaths) {
  const count = Number(accidentals || 0);
  if (!(Number(deaths) > 0) || count <= 0) return null;
  return <span className="inline-flex items-center gap-1.5 tracking-wide text-ghost uppercase">
            <Image src="/icons/backstab.png" alt="" width={14} height={14} />
            <span>
                <AnimatedStat value={count} />
            </span>
            <span>Martyrs</span>
        </span>;
}
function missionTotalSubtitle(total) {
  const n = Number(total || 0);
  if (n <= 0) return null;
  return <span className="inline-flex items-center gap-1.5 tracking-wide text-ghost uppercase">
            <span>
                <AnimatedStat value={n} />
            </span>
            <span>Total</span>
        </span>;
}

/**
 * Value for the merged EVENTS card — renders `W : L` with the wins
 * tinted success-green and the losses tinted danger-red. Reads as a
 * scoreline (Super Earth vs the enemy) rather than a fraction.
 */
function eventsScoreValue(wins, losses) {
  return <>
            <span className="text-success">
                <AnimatedStat value={wins} />
            </span>
            <span className="mx-1 text-ghost">:</span>
            <span className="text-danger">
                <AnimatedStat value={losses} />
            </span>
        </>;
}

/**
 * WAR_DURATION stat card. `seconds` is the elapsed time to display — total
 * war duration on the global tab, or how long a faction has been deployed on
 * a faction tab. `startUnix` is the Unix-seconds timestamp that span began —
 * war start on the global tab, faction introduction on a faction tab — shown
 * as a "DD MONTH" subtitle so the value and subtitle read as a coherent pair.
 * Null/invalid `seconds` (a faction not yet introduced) renders an em-dash
 * with no subtitle.
 *
 * @param {number | null} seconds - Elapsed war/deployment time in seconds
 * @param {number | null} startUnix - Unix-seconds timestamp the span began
 */
function warDurationCard(seconds, startUnix) {
  const valid = seconds != null && Number.isFinite(seconds) && seconds > 0;
  const days = valid ? Math.round(seconds / 86400) : null;
  const startValid = startUnix != null && Number.isFinite(startUnix) && startUnix > 0;
  return <StatCard label="WAR_DURATION" value={days != null ? `${days} ${days === 1 ? 'day' : 'days'}` : '—'} subtitle={valid && startValid ? <span className="tracking-wide text-ghost uppercase">
                        {formatStartDate(startUnix)}
                    </span> : undefined} />;
}

/**
 * A telemetry card for an archived season that predates stat collection.
 * Rather than a misleading zero, the value is censored and the subtitle
 * plays the gap as a Ministry of Truth redaction.
 */
function redactedCard(label) {
  return <StatCard label={label} value={<span className="text-ghost">████████</span>} subtitle={<span className="tracking-wide text-ghost uppercase">
                    Data redacted — Ministry of Truth
                </span>} />;
}

/**
 * Render a telemetry card, or its redacted stand-in when `redacted` is set —
 * an archived season with no h1_statistic data behind it.
 *
 * @param {boolean} redacted - Whether to censor the card
 * @param {string} label - The card label
 * @param {object} cardProps - Props for the real StatCard when not redacted
 */
function telemetryCard(redacted, label, cardProps) {
  return redacted ? redactedCard(label) : <StatCard label={label} {...cardProps} />;
}

/**
 * @typedef {{[key: string]: number | null}} PlayersAvg24h
 *   Per-faction 24h average player baselines, keyed by `'global'` and faction
 *   name (`'bugs'`, `'cyborgs'`, `'illuminate'`).
 *
 * @typedef {{[key: string]: { ago24h: number | null, ago48h: number | null }}} KillsTrend
 *   Per-faction cumulative kill baselines (~24h/48h ago), keyed by `'global'`
 *   and faction name.
 */

/**
 * @param {object} props - Component props.
 * @param {Array<{[key: string]: number}>} props.live - Latest per-faction statistic rows.
 * @param {string} props.faction - Active faction tab (`'global'` or a faction name).
 * @param {Array<import('@/features/dashboard/DashboardClient').LiveEvent>} [props.events] - Resolved/active events for the season.
 * @param {PlayersAvg24h | null} [props.playersAvg24h] - 24h player baselines, or null.
 * @param {KillsTrend | null} [props.killsTrend] - 24h/48h kill baselines, or null.
 * @param {number} [props.seasonDuration] - Total war duration in seconds.
 * @param {number | null} [props.warStart] - Unix-seconds war start, or null.
 * @param {boolean} [props.archived] - Whether this is an archived (historic) season.
 */
export default function StatGrid(t0) {
  const $ = _c(52);
  const {
    live,
    faction,
    events,
    playersAvg24h: t1,
    killsTrend: t2,
    seasonDuration: t3,
    warStart: t4,
    archived: t5
  } = t0;
  const playersAvg24h = t1 === undefined ? null : t1;
  const killsTrend = t2 === undefined ? null : t2;
  const seasonDuration = t3 === undefined ? 0 : t3;
  const warStart = t4 === undefined ? null : t4;
  const archived = t5 === undefined ? false : t5;
  if (!live?.length) {
    return null;
  }
  const factionIndex = faction !== "global" ? FACTION_INDEX[faction] : null;
  let t6;
  if ($[0] !== events || $[1] !== factionIndex) {
    const resolved = events?.filter(e => {
      if (factionIndex !== null && e.enemy !== factionIndex) {
        return false;
      }
      return e.status === EVENT_STATUS.SUCCESS || e.status === EVENT_STATUS.FAIL;
    }) ?? [];
    t6 = countOutcomes(resolved);
    $[0] = events;
    $[1] = factionIndex;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    wins,
    losses
  } = t6;
  const totalEvents = wins + losses;
  let t7;
  if ($[3] !== totalEvents) {
    t7 = totalEvents > 0 ? missionTotalSubtitle(totalEvents) : null;
    $[3] = totalEvents;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const eventsSubtitle = t7;
  if (faction === "global") {
    let t10;
    let t11;
    let t12;
    let t8;
    let t9;
    if ($[5] !== archived || $[6] !== killsTrend?.global || $[7] !== live || $[8] !== playersAvg24h?.global) {
      const totals = live.reduce(_temp, {
        players: 0,
        kills: 0,
        deaths: 0,
        accidentals: 0,
        won: 0,
        allMissions: 0
      });
      const onlineSubtitle = playersDeltaSubtitle(totals.players, playersAvg24h?.global);
      const killsSubtitle = killsTrendSubtitle(totals.kills, killsTrend?.global);
      const redacted = archived && totals.allMissions <= 0;
      t8 = "stat-grid";
      t9 = telemetryCard(redacted, "HELLDIVERS_ONLINE", {
        value: <AnimatedStat value={totals.players} />,
        subtitle: onlineSubtitle
      });
      t10 = telemetryCard(redacted, "ENEMIES_KILLED", {
        value: <AnimatedStat value={totals.kills} />,
        subtitle: killsSubtitle
      });
      t11 = telemetryCard(redacted, "HELLDIVERS_LOST", {
        value: <AnimatedStat value={totals.deaths} />,
        subtitle: accidentalSubtitle(totals.accidentals, totals.deaths),
        title: accidentalRateTooltip(totals.accidentals, totals.deaths)
      });
      t12 = telemetryCard(redacted, "MISSIONS_WON", {
        value: <AnimatedStat value={totals.won} />,
        subtitle: missionTotalSubtitle(totals.allMissions)
      });
      $[5] = archived;
      $[6] = killsTrend?.global;
      $[7] = live;
      $[8] = playersAvg24h?.global;
      $[9] = t10;
      $[10] = t11;
      $[11] = t12;
      $[12] = t8;
      $[13] = t9;
    } else {
      t10 = $[9];
      t11 = $[10];
      t12 = $[11];
      t8 = $[12];
      t9 = $[13];
    }
    let t13;
    if ($[14] !== losses || $[15] !== wins) {
      t13 = eventsScoreValue(wins, losses);
      $[14] = losses;
      $[15] = wins;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    let t14;
    if ($[17] !== eventsSubtitle || $[18] !== t13) {
      t14 = <StatCard label="EVENTS" value={t13} subtitle={eventsSubtitle} />;
      $[17] = eventsSubtitle;
      $[18] = t13;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    let t15;
    if ($[20] !== seasonDuration || $[21] !== warStart) {
      t15 = warDurationCard(seasonDuration, warStart);
      $[20] = seasonDuration;
      $[21] = warStart;
      $[22] = t15;
    } else {
      t15 = $[22];
    }
    let t16;
    if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t14 || $[27] !== t15 || $[28] !== t8 || $[29] !== t9) {
      t16 = <div className={t8}>{t9}{t10}{t11}{t12}{t14}{t15}</div>;
      $[23] = t10;
      $[24] = t11;
      $[25] = t12;
      $[26] = t14;
      $[27] = t15;
      $[28] = t8;
      $[29] = t9;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    return t16;
  }
  let t8;
  let t9;
  if ($[31] !== archived || $[32] !== eventsSubtitle || $[33] !== faction || $[34] !== factionIndex || $[35] !== killsTrend || $[36] !== live || $[37] !== losses || $[38] !== playersAvg24h || $[39] !== seasonDuration || $[40] !== warStart || $[41] !== wins) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t10;
      if ($[44] !== factionIndex) {
        t10 = s_0 => s_0.enemy === factionIndex;
        $[44] = factionIndex;
        $[45] = t10;
      } else {
        t10 = $[45];
      }
      const stats = live.find(t10);
      if (!stats) {
        t9 = null;
        break bb0;
      }
      const onlineSubtitle_0 = playersDeltaSubtitle(stats.players, playersAvg24h?.[faction]);
      const killsSubtitle_0 = killsTrendSubtitle(stats.kills, killsTrend?.[faction]);
      const redacted_0 = archived && Number(stats.missions) <= 0;
      const factionSeconds = stats.first_seen != null && warStart != null && Number.isFinite(warStart) ? seasonDuration - (stats.first_seen - warStart) : null;
      const t11 = telemetryCard(redacted_0, "HELLDIVERS_ONLINE", {
        value: <AnimatedStat value={stats.players} />,
        subtitle: onlineSubtitle_0
      });
      const t12 = telemetryCard(redacted_0, "ENEMIES_KILLED", {
        value: <AnimatedStat value={stats.kills} />,
        subtitle: killsSubtitle_0
      });
      const t13 = telemetryCard(redacted_0, "HELLDIVERS_LOST", {
        value: <AnimatedStat value={stats.deaths} />,
        subtitle: accidentalSubtitle(stats.accidentals, stats.deaths),
        title: accidentalRateTooltip(stats.accidentals, stats.deaths)
      });
      const t14 = telemetryCard(redacted_0, "MISSIONS_WON", {
        value: <AnimatedStat value={stats.successful_missions} />,
        subtitle: missionTotalSubtitle(stats.missions)
      });
      let t15;
      if ($[46] !== losses || $[47] !== wins) {
        t15 = eventsScoreValue(wins, losses);
        $[46] = losses;
        $[47] = wins;
        $[48] = t15;
      } else {
        t15 = $[48];
      }
      let t16;
      if ($[49] !== eventsSubtitle || $[50] !== t15) {
        t16 = <StatCard label="EVENTS" value={t15} subtitle={eventsSubtitle} />;
        $[49] = eventsSubtitle;
        $[50] = t15;
        $[51] = t16;
      } else {
        t16 = $[51];
      }
      t8 = <div className="stat-grid">{t11}{t12}{t13}{t14}{t16}{warDurationCard(factionSeconds, stats.first_seen)}</div>;
    }
    $[31] = archived;
    $[32] = eventsSubtitle;
    $[33] = faction;
    $[34] = factionIndex;
    $[35] = killsTrend;
    $[36] = live;
    $[37] = losses;
    $[38] = playersAvg24h;
    $[39] = seasonDuration;
    $[40] = warStart;
    $[41] = wins;
    $[42] = t8;
    $[43] = t9;
  } else {
    t8 = $[42];
    t9 = $[43];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  return t8;
}

/**
 * A single telemetry card: a label, a value, and an optional subtitle, with an
 * optional colored accent line and click handler.
 *
 * @param {object} props - Component props.
 * @param {import('react').ReactNode} props.label - The card label.
 * @param {import('react').ReactNode} props.value - The card value.
 * @param {import('react').ReactNode} [props.subtitle] - Optional subtitle line.
 * @param {'success' | 'danger'} [props.accentColor] - Optional accent-line tint.
 * @param {'success' | 'danger'} [props.valueColor] - Optional value-text tint.
 * @param {(() => void)} [props.onClick] - Optional click handler.
 * @param {string} [props.title] - Optional native tooltip text.
 */
function _temp(acc, s) {
  return {
    players: acc.players + Number(s.players || 0),
    kills: acc.kills + Number(s.kills || 0),
    deaths: acc.deaths + Number(s.deaths || 0),
    accidentals: acc.accidentals + Number(s.accidentals || 0),
    won: acc.won + Number(s.successful_missions || 0),
    allMissions: acc.allMissions + Number(s.missions || 0)
  };
}
export function StatCard(t0) {
  const $ = _c(19);
  const {
    label,
    value,
    subtitle,
    accentColor,
    valueColor,
    onClick,
    title
  } = t0;
  const accentClass = accentColor === "success" ? "stat-card-accent-success" : accentColor === "danger" ? "stat-card-accent-danger" : "stat-card-accent";
  const valueColorClass = valueColor === "success" ? "text-success" : valueColor === "danger" ? "text-danger" : "";
  const t1 = `stat-card${onClick ? " stat-card-clickable" : ""}`;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="stat-card-label">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `stat-card-value ${valueColorClass}`;
  let t4;
  if ($[2] !== t3 || $[3] !== value) {
    t4 = <span className={t3}>{value}</span>;
    $[2] = t3;
    $[3] = value;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== subtitle) {
    t5 = subtitle && <span className="stat-card-subtitle">{subtitle}</span>;
    $[5] = subtitle;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t4 || $[9] !== t5) {
    t6 = <div className="stat-card-content">{t2}{t4}{t5}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== accentClass) {
    t7 = <div className={accentClass} />;
    $[11] = accentClass;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== onClick || $[14] !== t1 || $[15] !== t6 || $[16] !== t7 || $[17] !== title) {
    t8 = <div className={t1} onClick={onClick} title={title}>{t6}{t7}</div>;
    $[13] = onClick;
    $[14] = t1;
    $[15] = t6;
    $[16] = t7;
    $[17] = title;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}